import { Button } from "@ai-teach/ui-web";
import { useQuery } from "@apollo/client/react";
import { useState } from "react";
import { ReviewComplianceDocument } from "../../../generated/graphql";

interface ReviewComplianceTableProps {
  schoolId: string;
}

export function ReviewComplianceTable({ schoolId }: ReviewComplianceTableProps) {
  const [page, setPage] = useState(0);
  const limit = 20;

  const {
    data,
    loading: isLoading,
    error,
  } = useQuery(ReviewComplianceDocument, {
    variables: {
      schoolId,
      limit,
      offset: page * limit,
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-500">Loading compliance data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-red-500">Error loading data: {error.message}</div>
      </div>
    );
  }

  if (!data?.reviewCompliance || data.reviewCompliance.students.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-500">No compliance data available</div>
      </div>
    );
  }

  const { students, total } = data.reviewCompliance;
  const totalPages = Math.ceil(total / limit);

  return (
    <div className="data-table-container">
      <div className="data-table-header">
        <h4 className="h6">Review Compliance</h4>
      </div>
      <div className="overflow-x-auto gradient-scrollbar">
        <table className="data-table">
          <thead>
            <tr>
              <th>Student Name</th>
              <th>Overdue Cards</th>
              <th>Current Streak</th>
              <th>Last Review</th>
            </tr>
          </thead>
          <tbody>
            {students.map((student) => (
              <tr key={student.studentId}>
                <td>
                  <div className="data-table-cell-name">{student.studentName}</div>
                </td>
                <td>
                  {student.overdueCount > 0 ? (
                    <span className="badge-danger">{student.overdueCount}</span>
                  ) : (
                    <span className="text-gray-400">{student.overdueCount}</span>
                  )}
                </td>
                <td>
                  {student.streak > 0 ? (
                    <span className="badge-primary">{student.streak} days</span>
                  ) : (
                    <span className="text-gray-400">{student.streak} days</span>
                  )}
                </td>
                <td>
                  <span className="data-table-cell-caption">
                    {student.lastReviewDate
                      ? new Date(student.lastReviewDate).toLocaleDateString()
                      : "Never"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-800">
          <div className="caption">
            Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total} students
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 0}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages - 1}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
